import React, { useCallback, useRef, useState } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Box from "@mui/material/Box";
import Fab from "@mui/material/Fab";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import {
  ChatRoomRowFragment,
  MessageRole,
  useChatRoomQuery,
  useSendChatMutation,
} from "generated/graphql";
import { useSnackbar } from "hooks";
import moment from "moment";
import { displayDateFormat } from "utils/const";
import { createdByToName, getUserIconText } from "utils/helper";
import { ChatMessageInputForm } from "./chat-input";
import { Message, MessageColor, MessagePosition } from "./chat-message";

const StyledPaper = styled(Paper)({
  width: "100vw",
  height: "calc(100vh - 100px)",
  position: "relative",
  display: "flex",
  alignItems: "center",
  flexDirection: "column",
});

interface ChatProps {
  chatRoomId: string;
  user: string;
}

export const Chat: React.FC<ChatProps> = (props) => {
  const { chatRoomId, user } = props;

  const [chatHistories, setChatHistories] = useState<ChatRoomRowFragment["ChatHistories"]>([]);

  const [shouldSticky, setShouldSticky] = useState(true);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const { displaySnackbar } = useSnackbar();

  const { data: chatRoomData } = useChatRoomQuery({
    variables: { id: chatRoomId },
    skip: !chatRoomId,
    onCompleted: (data) => {
      setChatHistories(data.chatRoom.ChatHistories);

      // auto scroll to bottom
      scrollToBottom(false);
    },
    onError: (error) => {
      displaySnackbar("error", error.message);
    },
  });

  const [sendChat] = useSendChatMutation({
    onCompleted: (data) => {
      setChatHistories(data.sendChat.ChatHistories);
      scrollToBottom(false);
    },
    onError: (error) => {
      displaySnackbar("error", error.message);
    },
  });

  const scrollToBottom = useCallback(
    (smooth = true) => {
      if (messagesContainerRef.current)
        messagesContainerRef.current?.scrollBy({
          left: 0,
          top: messagesContainerRef.current?.scrollHeight,
          behavior: smooth ? "smooth" : "auto",
        });
    },
    [messagesContainerRef],
  );

  const handleScroll = useCallback(
    function autoSetSticky(e: React.UIEvent<HTMLDivElement>) {
      // if (messagesContainerRef.current)
      const scrollHeight = e.currentTarget.scrollHeight;
      const bottomScrollPosition = e.currentTarget.scrollTop + e.currentTarget.clientHeight; // from 0 to scrollHeight

      if (scrollHeight - bottomScrollPosition < 30) setShouldSticky(true);
      else setShouldSticky(false);
    },
    [setShouldSticky],
  );

  const appendChatHistory = (chatHistory: ChatRoomRowFragment["ChatHistories"][number]) => {
    setChatHistories([...chatHistories, chatHistory]);
    scrollToBottom(false);
  };

  const sendMessage = async (message: string) => {
    const newChatHistory: ChatRoomRowFragment["ChatHistories"][number] = {
      id: moment().format(displayDateFormat), // generate temp id for frontend display message before request finish
      message: message,
      createdBy: user.toString(),
      role: MessageRole.user,
      createdAt: new Date().toISOString(),
      chatRoomId,
    };

    appendChatHistory(newChatHistory);

    sendChat({ variables: { chatRoomId, message } });
  };

  if (!chatRoomData) return null;

  const { chatRoom } = chatRoomData;

  return (
    <Box display="flex" alignItems="center" justifyContent="center">
      {props.chatRoomId ? (
        <StyledPaper>
          <Typography variant="h3">{chatRoom.name}</Typography>

          <Box
            ref={messagesContainerRef}
            width="100%"
            height="100%"
            margin={0}
            padding={{ xs: "10px", sm: "20px" }}
            borderTop="solid 1px grey"
            onScroll={handleScroll}
            sx={{ overflowY: "scroll" }}
          >
            {chatHistories.map((chatHistory) => {
              const messageColor = chatHistory.createdBy
                ? chatHistory.createdBy === props.user
                  ? MessageColor.blue
                  : MessageColor.yellow
                : MessageColor.grey;

              const messagePosition =
                chatHistory.createdBy === props.user ? MessagePosition.right : MessagePosition.left;
              return (
                <Message
                  key={chatHistory.id}
                  message={chatHistory.message}
                  createdAt={new Date()}
                  displayName={createdByToName(chatHistory.createdBy)}
                  color={messageColor}
                  position={messagePosition}
                  iconText={getUserIconText(chatHistory.createdBy)}
                />
              );
            })}
          </Box>

          <Box width="100%" position="relative">
            {!shouldSticky && (
              <Fab
                size="small"
                color="primary"
                onClick={() => scrollToBottom()}
                sx={{
                  position: "absolute",
                  top: "-50px",
                  right: { xs: "10px", sm: "20px" },
                }}
              >
                <KeyboardArrowDownIcon />
              </Fab>
            )}
            <ChatMessageInputForm sendMessage={sendMessage} />
          </Box>
        </StyledPaper>
      ) : (
        <StyledPaper>
          <Typography variant="h3">Please choose a chatroom</Typography>
        </StyledPaper>
      )}
    </Box>
  );
};
